/**
 * Modello della superficie **Attivita' SLA** — F3c (piano Client sez. 5 F3c,
 * sez. 6 riga 88, sez. 8 riga 127; decisione 2 in sez. 14.2).
 *
 * Dove vive: **non** nasce una pagina nuova. La superficie e' un innesto sulla
 * pagina attivita' **esistente** (`src/pages/MyActivities.tsx`, sez. 3 riga
 * "Attivita'"), cosi' lo SLA e' visibile **gia' in ToDo** come chiede il
 * criterio F3c; il dettaglio e' quello esistente (`TaskDetailDialog`) e la rotta
 * di atterraggio e' `myactivities/task/:taskId` (`src/App.tsx`, decisione 2).
 *
 * Cosa il modello garantisce (criteri F3c):
 * - **countdown nella base lavorativa**: il residuo e' `remaining_working_seconds`
 *   del payload meno il tempo **trascorso da `as_of`** (istante dichiarato dal
 *   Server), mai un calcolo di calendario nel browser (sez. 1 e sez. 4 F1). Se il
 *   payload dichiara `clock_running = false` (chiusura di calendario) il residuo
 *   **non scende** e riprende quando il Server riapre il clock: la riapertura non
 *   si calcola qui, si **richiede** (refetch a `next_clock_change_at`, sez. 5 F3d);
 * - **banner su deadline reali**: il banner ordina per `effective_deadline_at`
 *   del payload e mostra la salute del payload (`SLA_HEALTH_*`) con la scadenza
 *   vigente nella timezone dichiarata dalla riga. **Non esiste alcuna nozione di
 *   "oggi"**: "a rischio" e' una soglia percentuale sul residuo lato Server, non
 *   un giorno, e il modello non ha funzioni che confrontino la scadenza con il
 *   giorno corrente del browser (`SLA_ACTIVITIES_BANNER_RULE`);
 * - **dettaglio con originale/vigente e override**: `original_deadline_at` e
 *   `effective_deadline_at` sono entrambe rese e la vigente e' etichettata come
 *   modificata quando `overridden` e' vero. Il **motivo** dell'override non e'
 *   nel payload istanza (residuo dichiarato, vedi `SLA_ACTIVITY_OVERRIDE_REASON_SOURCE`);
 * - **preferenza dismiss per utente**: `greetingdismissedat` di
 *   `sla_user_preferences` e' per (utente, organizzazione) e la rotta
 *   `PATCH /sla/preferences` e' dell'utente autenticato (nessun `iduser`
 *   parametro: lo identifica il JWT).
 *
 * Riuso e non duplicazione: salute, durata lavorativa, istanti, freschezza ed
 * escalation ("livello n/m") sono gia' implementati una volta in
 * `model/slaMonitorModel.ts` (F3a) e sono qui **importati**, non riscritti: le
 * regole del contratto sono le stesse (un breach escalato resta un breach,
 * sez. 14.1).
 */

import dayjs from "dayjs";
import {
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
  SLA_HEALTH_ON_TIME,
  SLA_TARGET_ACTIVITY,
  SLA_TARGET_PROCESS,
  type SlaHealth,
  type SlaInstance,
  type SlaUserPreferencesDto,
  type SlaUserPreferencesInput,
} from "model/slaModel";
import {
  SLA_MONITOR_STALE_AFTER_SECONDS,
  isSlaMonitorStale,
  slaMonitorDateTime,
  slaMonitorEscalation,
  slaMonitorEscalationValue,
  slaMonitorHealth,
  slaMonitorHealthKey,
  slaMonitorOldestAsOf,
  type SlaMonitorEscalation,
  type SlaMonitorUserRow,
} from "model/slaMonitorModel";

// ─── Chiavi di traduzione (manifest Attivita') ───────────────────────────────

/**
 * Chiavi usate dalla superficie Attivita'. Raccolte qui perche' il piano sez. 4
 * le fa concordare con Python P1 in un manifest: `getTranslation` restituisce la
 * chiave quando il testo manca, quindi questo elenco **e'** il manifest lato
 * Attivita'. La famiglia `sla.activities.*` non e' ancora nel catalogo P1
 * (residuo dichiarato): finche' manca, le label escono grezze e l'asserzione
 * "nessuna chiave residua" le **nomina** invece di far passare il test per caso.
 */
export const SLA_ACTIVITIES_TEXT_KEYS = {
  /** Etichetta della strip di stato mostrata in cima alla pagina attivita'. */
  title: "sla.activities.title",
  /** Badge sulla riga ToDo: lo SLA e' visibile **gia' in ToDo** (sez. 5 F3c). */
  todoLabel: "sla.activities.todo.label",
  /** Banner: titolo, label delle due grandezze e comando di chiusura. */
  bannerTitle: "sla.activities.banner.title",
  bannerDeadline: "sla.activities.banner.deadline",
  bannerWorkingTime: "sla.activities.banner.working_time",
  bannerDismiss: "sla.activities.banner.dismiss",
  /** Countdown: label, stato in pausa e istante di ripresa dichiarato. */
  countdownLabel: "sla.activities.countdown.label",
  countdownPaused: "sla.activities.countdown.paused",
  countdownResumes: "sla.activities.countdown.resumes",
  countdownNotAvailable: "sla.activities.countdown.not_available",
  /** Dettaglio: originale, vigente, override e motivo. */
  detailTitle: "sla.activities.detail.title",
  detailOriginal: "sla.activities.detail.original",
  detailEffective: "sla.activities.detail.effective",
  detailOverridden: "sla.activities.detail.overridden",
  detailOverrideReason: "sla.activities.detail.override_reason",
  detailOverrideReasonUnavailable: "sla.activities.detail.override_reason.unavailable",
  detailRevision: "sla.activities.detail.revision",
  /** Preferenze per utente (dismiss, digest, escalation, transazionali). */
  preferencesTitle: "sla.activities.preferences.title",
  preferencesDigest: "sla.activities.preferences.digest",
  preferencesEscalation: "sla.activities.preferences.escalation",
  preferencesTransactional: "sla.activities.preferences.transactional",
  preferencesForbidden: "sla.activities.preferences.forbidden",
  preferencesError: "sla.activities.preferences.error",
  /** Stati di caricamento/errore/empty della superficie. */
  loading: "sla.activities.loading",
  empty: "sla.activities.empty",
  loadError: "sla.activities.load_error",
  forbidden: "sla.activities.forbidden",
} as const;

/**
 * Chiavi **gia' esistenti** riusate: nessuna di queste va duplicata in
 * `sla.activities.*` (sez. 4: non duplicare il catalogo). Le tre label di salute
 * e il "livello n/m" sono gli stessi del Monitor perche' lo stato del contratto
 * e' lo stesso; `as_of`, `timezone` e `stale` sono le stesse grandezze.
 */
export const SLA_ACTIVITIES_REUSED_KEYS: string[] = [
  "generic.hours.caption",
  "generic.minutes.caption",
  "generic.seconds.caption",
  "sla.editor.fixture_notice",
  "sla.monitor.as_of",
  "sla.monitor.escalation.label",
  "sla.monitor.escalation.none",
  "sla.monitor.health.at_risk",
  "sla.monitor.health.breached",
  "sla.monitor.health.on_time",
  "sla.monitor.overridden",
  "sla.monitor.stale",
  "sla.monitor.timezone",
];

// ─── Riuso dichiarato ────────────────────────────────────────────────────────

/**
 * Il banner **non** ha una nozione di "oggi": sceglie e mostra solo dati del
 * payload (salute e `effective_deadline_at`). Se in futuro servisse una nozione
 * di giorno, sarebbe una decisione di contratto (soglia percentuale gia' lato
 * Server), non una sottrazione sul tempo del browser.
 */
export const SLA_ACTIVITIES_BANNER_RULE = "payload_deadline_and_health_only" as const;

/**
 * Il motivo dell'override **non e' nel payload**. `SlaInstance`
 * (`src/model/slaModel.ts:231-266`) porta `overridden` ma non il motivo: la
 * `reason` vive solo nella richiesta `SlaOverrideRequest` (`slaModel.ts:343-346`).
 * Il Client non puo' quindi rendere l'override "auditato" per intero: rende le
 * due date e la distinzione, e **dichiara** l'assenza del motivo. Residuo
 * bloccante lato Server (campo mancante nella lista istanze) o lettura di
 * `sla_event`, entrambi fuori dal perimetro Client.
 */
export const SLA_ACTIVITY_OVERRIDE_REASON_SOURCE = "missing_in_instance_payload" as const;

/**
 * Salute che entrano nel banner: un'istanza `on_time` non produce banner. Non e'
 * un quarto stato: e' il sottoinsieme dei tre stati del contratto che merita
 * attenzione (sez. 11.1).
 */
export const SLA_ACTIVITIES_BANNER_HEALTHS: readonly SlaHealth[] = [
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
];

// ─── Collegamento istanza -> task della lista attivita' ──────────────────────

/**
 * Identita' minima di un task della pagina attivita' necessaria al collegamento
 * (`Task` in `src/model/activitiesModel.ts:25-48`): il processo e il nodo BPMN.
 */
export interface SlaActivityTaskIdentity {
  idprocess: number;
  designerid?: string | null;
}

/**
 * true quando l'istanza porta davvero uno SLA da mostrare: almeno una scadenza
 * (originale o vigente) o un residuo/ritardo lavorativo. Un'istanza senza
 * nessuno di questi dati equivale a "nessuno SLA": il blocco SLA (badge,
 * dettaglio, riga riassuntiva) non va reso, invece di mostrare segnaposto vuoti
 * ("Tempo restante / Scadenza / Escalation: nessuna").
 */
export const slaInstanceHasSla = (
  instance: Pick<
    SlaInstance,
    | "original_deadline_at"
    | "effective_deadline_at"
    | "remaining_working_seconds"
    | "overdue_working_seconds"
  >,
): boolean =>
  Boolean(instance.effective_deadline_at) ||
  Boolean(instance.original_deadline_at) ||
  (instance.remaining_working_seconds !== null &&
    instance.remaining_working_seconds !== undefined) ||
  (instance.overdue_working_seconds !== null &&
    instance.overdue_working_seconds !== undefined);

/**
 * Un'istanza si collega a un task quando sono lo stesso processo e, per i target
 * di **attivita'** (`target_type = 2`), lo stesso nodo (`designerid`, che il
 * Server confronta con `target_designerid`). Un target di **processo**
 * (`target_type = 1`, `target_designerid` NULL) vale per tutti i task aperti di
 * quel processo: e' esattamente la semantica del contratto C1 e non una
 * deduzione del browser.
 */
export const slaActivityMatchesTask = (
  instance: Pick<SlaInstance, "idprocess" | "target_type" | "target_designerid">,
  task: SlaActivityTaskIdentity,
): boolean => {
  if (instance.idprocess !== task.idprocess) return false;
  if (instance.target_type === SLA_TARGET_PROCESS) return true;
  // Target sconosciuto/assente: non e' uno SLA applicabile al task.
  if (instance.target_type !== SLA_TARGET_ACTIVITY) return false;
  return Boolean(
    instance.target_designerid &&
      task.designerid &&
      instance.target_designerid === task.designerid,
  );
};

/**
 * Istanza che governa un task. Se piu' istanze corrispondono (target di processo
 * **e** di attivita' sullo stesso processo), vince il target **di attivita'**:
 * e' la promessa piu' specifica e la stessa che il Monitor mostra come riga
 * dedicata (sez. 5 F3a: "una riga per istanza").
 */
export const slaActivityForTask = (
  instances: SlaInstance[],
  task: SlaActivityTaskIdentity,
): SlaInstance | null => {
  const matches = instances.filter(
    (instance) => slaInstanceHasSla(instance) && slaActivityMatchesTask(instance, task),
  );
  if (matches.length === 0) return null;
  return matches.find((instance) => instance.target_type === SLA_TARGET_ACTIVITY) || matches[0];
};

/**
 * Istanza **di processo** (`target_type = 1`) aperta per un processo, `null`
 * se non c'e'. Serve quando il task e' governato da uno SLA di attivita': la
 * promessa del processo resta viva e va mostrata come informazione secondaria
 * invece di sparire dietro quella piu' specifica.
 */
export const slaProcessInstanceFor = (
  instances: SlaInstance[],
  idprocess: number,
): SlaInstance | null =>
  instances.find(
    (instance) =>
      instance.idprocess === idprocess &&
      instance.target_type === SLA_TARGET_PROCESS &&
      slaInstanceHasSla(instance),
  ) ?? null;

/** Gravita' ordinabile della salute: `null` (stato non reso) vale meno di tutto. */
export const slaHealthSeverity = (health: SlaHealth | null): number =>
  health === null ? -1 : health;

/** Task della lista attivita' che hanno uno SLA attivo (per il conteggio/empty). */
export const slaActivityTasksWithSla = <T extends SlaActivityTaskIdentity>(
  instances: SlaInstance[],
  tasks: T[],
): T[] => tasks.filter((task) => slaActivityForTask(instances, task) !== null);

// ─── Filtro per salute nella lista attivita' ─────────────────────────────────

/**
 * Istanze raggruppate per processo: il collegamento istanza -> task confronta
 * prima `idprocess`, quindi l'indice evita di scorrere tutte le istanze per
 * ogni task (la lista attivita' puo' contare decine di migliaia di righe).
 */
export const slaActivityInstancesByProcess = (
  instances: SlaInstance[],
): Map<number, SlaInstance[]> => {
  const byProcess = new Map<number, SlaInstance[]>();
  instances.forEach((instance) => {
    const list = byProcess.get(instance.idprocess);
    if (list) list.push(instance);
    else byProcess.set(instance.idprocess, [instance]);
  });
  return byProcess;
};

/**
 * Salute resa di un task: quella dell'istanza che lo governa
 * (`slaActivityForTask`) seguita dal countdown locale (`slaActivityLiveHealth`),
 * cioe' la stessa del badge sulla card. `null` senza SLA o salute sconosciuta.
 */
export const slaActivityTaskHealth = (
  byProcess: Map<number, SlaInstance[]>,
  task: SlaActivityTaskIdentity,
  nowMs: number,
): SlaHealth | null => {
  const candidates = byProcess.get(task.idprocess);
  if (!candidates) return null;
  const instance = slaActivityForTask(candidates, task);
  return instance ? slaActivityLiveHealth(instance, nowMs) : null;
};

/** Conteggi dei chip di salute sui task in lista. */
export interface SlaActivityHealthCounts {
  /** Tutti i task in lista (con e senza SLA): e' cio' che "Totale" mostra. */
  total: number;
  on_time: number;
  at_risk: number;
  breached: number;
}

/**
 * Conteggi dei chip a partire dalle saluti dei task in lista. "Totale" conta
 * tutte le righe perche' e' il chip di "nessun filtro": il numero coincide con
 * le righe mostrate quando e' selezionato. Task senza SLA o con salute
 * sconosciuta entrano solo nel totale.
 */
export const slaActivityHealthCounts = (
  healths: Array<SlaHealth | null>,
): SlaActivityHealthCounts =>
  healths.reduce<SlaActivityHealthCounts>(
    (counts, health) => {
      counts.total += 1;
      if (health === SLA_HEALTH_ON_TIME) counts.on_time += 1;
      else if (health === SLA_HEALTH_AT_RISK) counts.at_risk += 1;
      else if (health === SLA_HEALTH_BREACHED) counts.breached += 1;
      return counts;
    },
    { total: 0, on_time: 0, at_risk: 0, breached: 0 },
  );

// ─── Countdown nella base lavorativa ─────────────────────────────────────────

/** Attributi DOM del countdown: numeri del payload, non testo formattato. */
export interface SlaActivityCountdownView {
  /** Secondi lavorativi residui: `remaining_working_seconds` meno il trascorso da `as_of`. */
  remainingSeconds: number | null;
  /** Secondi lavorativi di ritardo: `overdue_working_seconds` piu' l'eccedenza maturata. */
  overdueSeconds: number | null;
  /** true quando il payload dichiara il clock fermo (`clock_running === false`). */
  paused: boolean;
  /** true quando il clock dichiarato e' in corsa: solo allora il residuo scende. */
  running: boolean;
  /** Istante dichiarato di prossimo cambio di clock (pausa o ripresa). */
  nextClockChangeAt: string | null;
  /** true quando il browser ha superato `next_clock_change_at`: serve un refetch (sez. 5 F3d). */
  needsRefetch: boolean;
}

/**
 * Countdown lavorativo. L'unica sottrazione ammessa e' fra l'orologio del browser
 * e l'istante **dichiarato dal Server** (`as_of`) — mai un calendario, mai un
 * giorno solare (sez. 1, sez. 4 F1). Con `clock_running = false` il tempo
 * trascorso **non** si applica: il residuo resta quello del payload finche' il
 * Server non dichiara la riapertura (`needsRefetch`).
 *
 * `remaining_working_seconds` NULL = il Server dichiara il residuo non
 * calcolabile: si rende N/D, non zero.
 */
export const slaActivityCountdown = (
  instance: Pick<
    SlaInstance,
    "remaining_working_seconds" | "overdue_working_seconds" | "clock_running" | "next_clock_change_at" | "as_of"
  >,
  nowMs: number,
): SlaActivityCountdownView => {
  const asOfMs = dayjs(instance.as_of).valueOf();
  const elapsedSeconds = Number.isFinite(asOfMs)
    ? Math.max(0, Math.floor((nowMs - asOfMs) / 1000))
    : 0;
  const running = instance.clock_running === true;
  const appliedSeconds = running ? elapsedSeconds : 0;

  const remainingRaw = instance.remaining_working_seconds;
  const remainingSeconds =
    remainingRaw === null || remainingRaw === undefined
      ? null
      : Math.max(0, remainingRaw - appliedSeconds);

  // Il ritardo cresce solo dell'eccedenza oltre il residuo: il payload porta gia'
  // il ritardo accumulato alla propria `as_of`.
  const overflow = remainingRaw === null || remainingRaw === undefined
    ? 0
    : Math.max(0, appliedSeconds - remainingRaw);
  const overdueSeconds =
    instance.overdue_working_seconds === null || instance.overdue_working_seconds === undefined
      ? null
      : instance.overdue_working_seconds + overflow;

  const changeMs = instance.next_clock_change_at
    ? dayjs(instance.next_clock_change_at).valueOf()
    : Number.NaN;
  const needsRefetch = Number.isFinite(changeMs) && nowMs >= changeMs;

  return {
    remainingSeconds,
    overdueSeconds,
    paused: instance.clock_running === false,
    running,
    nextClockChangeAt: instance.next_clock_change_at ?? null,
    needsRefetch,
  };
};

// ─── Salute resa: payload aggiornato dal countdown locale ────────────────────

type SlaActivityLiveFields = Pick<
  SlaInstance,
  | "id"
  | "health"
  | "risk_at"
  | "effective_deadline_at"
  | "remaining_working_seconds"
  | "overdue_working_seconds"
  | "clock_running"
  | "next_clock_change_at"
  | "as_of"
>;

const instantMs = (value: string | null | undefined): number =>
  value ? dayjs(value).valueOf() : Number.NaN;

/**
 * Salute da **rendere**. La salute del payload e' quella del Server alla sua
 * `as_of`, ma il countdown scende nel browser: senza questa derivazione un task
 * con la scadenza superata resterebbe "in tempo" fino al prossimo refetch.
 *
 * Regole (mai migliorare il payload, solo peggiorarlo):
 * - con il clock **in corsa**, residuo vivo a 0 e ritardo > 0 => `breached`;
 * - con il clock in corsa e l'istante `risk_at` dichiarato dal Server superato
 *   => almeno `at_risk` (l'istante e' del Server: nessun calcolo di calendario);
 * - clock fermo o salute del payload gia' peggiore: vale il payload.
 *
 * E' solo una resa provvisoria: il passaggio di soglia richiede anche un refetch
 * (`slaActivityThresholdCrossings`), e il Server resta la fonte della verita'.
 */
export const slaActivityLiveHealth = (
  instance: SlaActivityLiveFields,
  nowMs: number,
): SlaHealth | null => {
  const health = slaMonitorHealth(instance.health);
  if (health === null || health === SLA_HEALTH_BREACHED) return health;
  const countdown = slaActivityCountdown(instance, nowMs);
  if (!countdown.running) return health;
  if (countdown.remainingSeconds === 0 && (countdown.overdueSeconds ?? 0) > 0) {
    return SLA_HEALTH_BREACHED;
  }
  const riskMs = instantMs(instance.risk_at);
  if (health === SLA_HEALTH_ON_TIME && Number.isFinite(riskMs) && nowMs >= riskMs) {
    return SLA_HEALTH_AT_RISK;
  }
  return health;
};

/**
 * Soglie (scadenza, `risk_at`) che il countdown locale ha **attraversato** dopo
 * la `as_of` del payload, con il clock in corsa. Ogni soglia ha una chiave
 * stabile (istanza + soglia + istante dichiarato) cosi' il provider rilegge una
 * volta sola per attraversamento, senza cicli di refetch se il Server risponde
 * con lo stesso payload. Soglie gia' superate alla `as_of` non contano: il Server
 * le ha gia' viste.
 */
export const slaActivityThresholdCrossings = (
  instance: SlaActivityLiveFields,
  nowMs: number,
): string[] => {
  const health = slaMonitorHealth(instance.health);
  if (health === null || health === SLA_HEALTH_BREACHED) return [];
  const countdown = slaActivityCountdown(instance, nowMs);
  if (!countdown.running) return [];
  const crossings: string[] = [];
  const remainingRaw = instance.remaining_working_seconds;
  if (
    remainingRaw !== null &&
    remainingRaw !== undefined &&
    remainingRaw > 0 &&
    countdown.remainingSeconds === 0
  ) {
    crossings.push(`${instance.id}:deadline:${instance.effective_deadline_at ?? ""}:${remainingRaw}`);
  }
  const riskMs = instantMs(instance.risk_at);
  const asOfMs = instantMs(instance.as_of);
  if (
    health === SLA_HEALTH_ON_TIME &&
    Number.isFinite(riskMs) &&
    Number.isFinite(asOfMs) &&
    asOfMs < riskMs &&
    nowMs >= riskMs
  ) {
    crossings.push(`${instance.id}:risk:${instance.risk_at}`);
  }
  return crossings;
};

// ─── Banner ──────────────────────────────────────────────────────────────────

/**
 * Riga del banner: dati del payload; la salute e' quella resa
 * (`slaActivityLiveHealth`), che puo' solo peggiorare quella del payload.
 */
export interface SlaActivityBannerView {
  instanceId: number;
  /** Processo della riga: serve a mostrare lo SLA di processo accanto a quello di attivita'. */
  idprocess: number;
  /** true quando la riga e' uno SLA di attivita' (`target_type = 2`). */
  activityTarget: boolean;
  health: SlaHealth;
  /** Chiave di traduzione dello stato: la stessa dei tre stati del contratto. */
  healthKey: string;
  effectiveDeadlineAt: string | null;
  /** Scadenza vigente resa nella timezone **dichiarata** dalla riga. */
  deadlineLabel: string;
  timezoneId: string;
  countdown: SlaActivityCountdownView;
  processDescription: string;
  activityDescription: string;
  asOf: string;
}

const bannerRank = (health: SlaHealth): number =>
  health === SLA_HEALTH_BREACHED ? 0 : 1;

/**
 * Riga da mettere nel banner: la piu' grave del payload (`breached` prima di
 * `at_risk`) e, a pari gravita', quella con la `effective_deadline_at` piu'
 * vicina. **Non** si guarda il giorno corrente: se una istanza e' "a rischio" con
 * scadenza fra tre giorni, il banner lo dice con la data, non "scade oggi".
 * `null` quando nessuna istanza e' a rischio o violata.
 */
export const slaActivityBanner = (
  instances: SlaInstance[],
  nowMs: number,
): SlaActivityBannerView | null => {
  const candidates = instances
    .map((instance) => ({ instance, health: slaActivityLiveHealth(instance, nowMs) }))
    .filter(
      (entry): entry is { instance: SlaInstance; health: SlaHealth } =>
        entry.health !== null && SLA_ACTIVITIES_BANNER_HEALTHS.includes(entry.health),
    )
    .sort((entryA, entryB) => {
      const a = entryA.instance;
      const b = entryB.instance;
      const byHealth = bannerRank(entryA.health) - bannerRank(entryB.health);
      if (byHealth !== 0) return byHealth;
      const aDeadline = dayjs(a.effective_deadline_at).valueOf();
      const bDeadline = dayjs(b.effective_deadline_at).valueOf();
      const aValid = Number.isFinite(aDeadline);
      const bValid = Number.isFinite(bDeadline);
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      return aDeadline - bDeadline;
    });

  const top = candidates[0];
  if (!top) return null;
  const { instance, health } = top;

  return {
    instanceId: instance.id,
    idprocess: instance.idprocess,
    activityTarget: instance.target_type === SLA_TARGET_ACTIVITY,
    health,
    healthKey: slaMonitorHealthKey(health),
    effectiveDeadlineAt: instance.effective_deadline_at,
    deadlineLabel: slaMonitorDateTime(
      instance.effective_deadline_at,
      instance.timezone_id,
    ),
    timezoneId: instance.timezone_id,
    countdown: slaActivityCountdown(instance, nowMs),
    processDescription: instance.process_description,
    activityDescription: instance.activity_description,
    asOf: instance.as_of,
  };
};

// ─── Dettaglio: originale, vigente, override ─────────────────────────────────

export interface SlaActivityDetailView {
  /** Promessa originale: le revisioni non la cancellano (contratto C1). */
  originalDeadlineAt: string | null;
  originalLabel: string;
  /** Scadenza vigente. */
  effectiveDeadlineAt: string | null;
  effectiveLabel: string;
  /** true quando la vigente diverge dall'originale ed e' etichettata come modificata. */
  overridden: boolean;
  /** Motivo dell'override: **non** disponibile nel payload (residuo dichiarato). */
  overrideReason: string | null;
  /** true quando il payload dichiara l'override ma il motivo non e' leggibile lato Client. */
  overrideReasonUnavailable: boolean;
  revision: number;
  timezoneId: string;
  /** "livello n/m" o `null` quando nessun livello e' stato raggiunto (sez. 14.1). */
  escalationValue: string | null;
  escalation: SlaMonitorEscalation;
  health: SlaHealth | null;
  healthKey: string | null;
}

/**
 * Vista del dettaglio di un'istanza. Le due date ci sono entrambe e la vigente e'
 * etichettata come modificata quando `overridden` e' vero. Il motivo dell'override
 * **non** e' nel payload (`SLA_ACTIVITY_OVERRIDE_REASON_SOURCE`): la vista lo
 * dichiara non disponibile invece di mostrare un testo inventato.
 */
export const slaActivityDetail = (
  instance: SlaInstance,
  nowMs?: number,
): SlaActivityDetailView => {
  // Con `nowMs` la salute segue il countdown locale (`slaActivityLiveHealth`);
  // senza, resta quella del payload.
  const health =
    nowMs === undefined
      ? slaMonitorHealth(instance.health)
      : slaActivityLiveHealth(instance, nowMs);
  const escalation = slaMonitorEscalation(instance);
  return {
    originalDeadlineAt: instance.original_deadline_at,
    originalLabel: slaMonitorDateTime(
      instance.original_deadline_at,
      instance.timezone_id,
    ),
    effectiveDeadlineAt: instance.effective_deadline_at,
    effectiveLabel: slaMonitorDateTime(
      instance.effective_deadline_at,
      instance.timezone_id,
    ),
    overridden: instance.overridden,
    overrideReason: null,
    overrideReasonUnavailable: instance.overridden,
    revision: instance.revision,
    timezoneId: instance.timezone_id,
    escalationValue: slaMonitorEscalationValue(escalation),
    escalation,
    health,
    healthKey: health === null ? null : slaMonitorHealthKey(health),
  };
};

/** Nome leggibile dell'assegnatario: stessa regola del Monitor (F3a), nessun duplicato. */
export type SlaActivityUserRow = SlaMonitorUserRow;

// ─── Freschezza ──────────────────────────────────────────────────────────────

/**
 * Soglia di freschezza: la stessa scelta Client dichiarata dal Monitor
 * (`SLA_MONITOR_STALE_AFTER_SECONDS`, sez. 5 F3d) — un solo valore nel prodotto.
 */
export const SLA_ACTIVITIES_STALE_AFTER_SECONDS = SLA_MONITOR_STALE_AFTER_SECONDS;

/** L'istante piu' vecchio fra i payload resi governa la freschezza. */
export const slaActivityOldestAsOf = slaMonitorOldestAsOf;

/** true quando i dati resi non sono piu' aggiornati: lo stato non resta verde. */
export const isSlaActivityStale = isSlaMonitorStale;

// ─── Preferenze per utente ───────────────────────────────────────────────────

/** I tre interruttori individuali, indipendenti fra loro (decisione 2, sez. 14.3). */
export const SLA_ACTIVITY_PREFERENCE_KEYS = ["digest", "escalation", "transactional"] as const;
export type SlaActivityPreferenceKey = (typeof SLA_ACTIVITY_PREFERENCE_KEYS)[number];

/** Chiave di traduzione di ciascun interruttore (nessuna label come enum). */
export const SLA_ACTIVITY_PREFERENCE_TEXT_KEYS: Record<
  SlaActivityPreferenceKey,
  string
> = {
  digest: SLA_ACTIVITIES_TEXT_KEYS.preferencesDigest,
  escalation: SLA_ACTIVITIES_TEXT_KEYS.preferencesEscalation,
  transactional: SLA_ACTIVITIES_TEXT_KEYS.preferencesTransactional,
};

/** Nome del campo nel PATCH (`snake_case`, come lo legge il controller). */
export const SLA_ACTIVITY_PREFERENCE_FIELDS: Record<
  SlaActivityPreferenceKey,
  keyof SlaUserPreferencesInput
> = {
  digest: "digest_enabled",
  escalation: "escalation_enabled",
  transactional: "transactional_enabled",
};

/** Valore dichiarato dalla riga di preferenze. */
export const slaActivityPreferenceValue = (
  preferences: SlaUserPreferencesDto,
  key: SlaActivityPreferenceKey,
): boolean => {
  switch (key) {
    case "digest":
      return preferences.digestenabled;
    case "escalation":
      return preferences.escalationenabled;
    default:
      return preferences.transactionalenabled;
  }
};

/** Corpo del PATCH per un interruttore: solo il campo cambiato. */
export const slaActivityPreferencePatch = (
  key: SlaActivityPreferenceKey,
  value: boolean,
): SlaUserPreferencesInput => ({ [SLA_ACTIVITY_PREFERENCE_FIELDS[key]]: value });

/**
 * Il saluto e' chiuso **per l'utente autenticato**: `greetingdismissedat` e' NULL
 * finche' l'utente non lo chiude (la riga e' per utente+organizzazione e la rotta
 * `PATCH /sla/preferences` non prende `iduser`: lo identifica il JWT, sez. 14.3).
 */
export const slaActivityBannerDismissed = (
  preferences: SlaUserPreferencesDto | null,
): boolean => Boolean(preferences && preferences.greetingdismissedat);

/**
 * Ambito della preferenza, in chiaro: serve a mostrare che il dismiss vale per
 * (utente, organizzazione) e non per un dispositivo o un browser.
 */
export const slaActivityPreferencesScope = (
  preferences: Pick<SlaUserPreferencesDto, "iduser" | "idorganization">,
): string => `user:${preferences.iduser}@organization:${preferences.idorganization}`;

/** Corpo del PATCH del dismiss: la chiusura e' unica, il Server non registra la riapertura. */
export const SLA_ACTIVITY_DISMISS_PATCH: SlaUserPreferencesInput = {
  greeting_dismissed: true,
};

// ─── Limite dichiarato della superficie ──────────────────────────────────────

/**
 * La rotta istanze e' per **organizzazione** e paginata a `SLA_INSTANCES_PAGE_SIZE`
 * (50, sez. 5): la superficie Attivita' legge la **prima pagina** e collega le
 * righe ai task gia' visibili in ToDo/Doing. Se l'organizzazione avesse piu' di
 * una pagina di istanze aperte, un task oltre la prima pagina resterebbe senza
 * badge: limite dichiarato (`SLA_ACTIVITIES_INSTANCES_LIMIT`), non mascherato.
 * Nessun paging nella pagina attivita': non c'e' un comando che prometta di piu'.
 */
export const SLA_ACTIVITIES_INSTANCES_LIMIT = "first_page_only" as const;

/** Pagina letta dalla superficie (0-based, sez. 5). */
export const SLA_ACTIVITIES_INSTANCES_PAGE = 0 as const;

/** Stato locale della superficie, tenuto in un solo posto per tutti i consumatori. */
export interface SlaActivitiesState {
  instances: SlaInstance[];
  preferences: SlaUserPreferencesDto | null;
  loading: boolean;
  error: string | null;
  forbidden: boolean;
  asOf: string | null;
  stale: boolean;
  /** true quando le risposte sono simulate (avviso `sla.editor.fixture_notice`). */
  fixtures: boolean;
}
